import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {store} from '../../store';
import {displayPaymentErrorMessage, type OrderService} from '../order';
import {initAuthnetCardMethod} from './gateways/card';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {setupAuthnetButton} from './button';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {clearLocalSession} from '../session';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {$qsAll} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {initAuthnetEcheckMethod} from './gateways/echeck';
import {AuthnetButtonElement} from '../../../../native-checkout/ts/authnet/class-authnet-element';

/**
 * Implementation type for a Authnet gateway
 */
export type AuthnetGateway = {config: GatewayConfiguration};

export default function initAuthnetPaymentIntegration(orderService: OrderService) {
	const acceptSrc = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'accept_src');
	const clientKey = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'public_client_key');
	const loginId = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'login_id');

	if (!loginId || !clientKey || !acceptSrc) {
		return;
	}

	const buttonElement = new AuthnetButtonElement({
		loginId,
		clientKey,
		acceptSrc,
	});

	const gateways = registerAuthnetGateways();
	setupAuthnetButton();
	// transaction flow
	$qsAll('.authnet-btn', $el => {
		$el.addEventListener('click', async () => {
			store.dispatch(startModalProcessing());

			const gatewayId = PaymentConfiguration.selectedGateway();
			const gateway = gateways[gatewayId];
			if (!gateway) {
				return;
			}

			let type: 'card' | 'bank' = 'card';
			if (gatewayId === 'peachpay_authnet_echeck') {
				type = 'bank';
			}

			const {token, error, cancel} = await buttonElement.tokenize(type);

			const transaction = await orderService.startTransaction(gateway.config.gatewayId);
			if (!transaction) {
				store.dispatch(stopModalLoading());
				return;
			}

			if (cancel) {
				store.dispatch(stopModalLoading());
				return;
			}

			if (error ?? !token) {
				displayPaymentErrorMessage(error?.map(error => `${error.code}: ${error.message}`).join('\n') ?? 'An unknown error occured while tokenizing the payment method.');
				store.dispatch(stopModalLoading());
				return;
			}

			const order = await orderService.placeOrder(transaction, {
				peachpay_session_id: PeachPayOrder.sessionId(),
				peachpay_transaction_id: transaction.getId(),
				peachpay_authnet_data_descriptor: token.descriptor,
				peachpay_authnet_data_value: token.value,
			});

			if (order.result === 'failure') {
				displayPaymentErrorMessage(order.message);
				await transaction?.complete();
				store.dispatch(stopModalLoading());
				return;
			}

			store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

			updateCustomerDataInBrowser();
			clearLocalSession();

			const successURL = orderService.getOrderRedirect(order);
			if (window.top) {
				await transaction?.complete();
				window.top.location = successURL;
			}
		});
	});
}

function registerAuthnetGateways() {
	const gatewayImplementations: Record<string, AuthnetGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.AUTHNET_CARD_GATEWAY)) {
		const method = initAuthnetCardMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.AUTHNET_ECHECK_GATEWAY)) {
		const method = initAuthnetEcheckMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}
